import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Toast from './Toast';
import Modal from './Modal';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [modal, setModal] = useState(null);

  // Non-blocking notifications (toasts)
  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Blocking notifications (modals)
  const showModal = useCallback((config) => {
    return new Promise((resolve) => {
      setModal({
        ...config,
        onClose: (result) => {
          setModal(null);
          resolve(result);
        },
      });
    });
  }, []);

  const hideModal = useCallback(() => {
    setModal(null);
  }, []);

  // Convenience methods
  const showSuccess = useCallback(
    (message, duration) => showToast(message, 'success', duration),
    [showToast]
  );
  const showError = useCallback(
    (message, duration) => showToast(message, 'error', duration),
    [showToast]
  );
  const showWarning = useCallback(
    (message, duration) => showToast(message, 'warning', duration),
    [showToast]
  );
  const showInfo = useCallback(
    (message, duration) => showToast(message, 'info', duration),
    [showToast]
  );

  const showConfirm = useCallback(
    (message, title = 'Confirm') => {
      return showModal({
        type: 'confirm',
        title,
        message,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      });
    },
    [showModal]
  );

  const showAlert = useCallback(
    (message, title = 'Alert') => {
      return showModal({
        type: 'alert',
        title,
        message,
        confirmText: 'OK',
      });
    },
    [showModal]
  );

  const value = {
    // Toast methods
    showToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Modal methods
    showModal,
    hideModal,
    showConfirm,
    showAlert,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      {toasts.length > 0 &&
        createPortal(
          <div className="fixed right-4 top-4 z-[9999] space-y-2">
            {toasts.map((toast) => (
              <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
            ))}
          </div>,
          document.body
        )}

      {/* Modal Container */}
      {modal && createPortal(<Modal {...modal} />, document.body)}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
